import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaMicrophone, FaStop, FaPlay, FaPause, FaTimes, FaUpload } from 'react-icons/fa';

const MAX_RECORDING_SECONDS = 60; // Maximum recording duration in seconds

const AudioRecorder = ({ 
  isOpen, 
  onClose, 
  onSave, 
  uploadsEnabled = true 
}) => {
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const [nameText, setNameText] = useState('');
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const audioPlayerRef = useRef(null);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Cleanup function
  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
  }, [audioUrl]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      cleanup();
      setIsRecording(false);
      setIsPaused(false);
      setRecordingTime(0);
      setAudioBlob(null);
      setAudioUrl(null);
      setAudioLevel(0);
      setNameText('');
      setPermissionDenied(false);
      setIsPlaying(false);
      audioChunksRef.current = [];
    }
  }, [isOpen, cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  // Audio level visualization
  const visualizeAudio = useCallback(() => {
    if (!analyserRef.current) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    
    const updateLevel = () => {
      if (!analyserRef.current) return;
      
      analyserRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      setAudioLevel(average / 255);
      
      animationFrameRef.current = requestAnimationFrame(updateLevel);
    };
    
    updateLevel();
  }, []);

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });
      
      streamRef.current = stream;
      setPermissionDenied(false);

      // Set up audio analyser for visualization
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      analyser.fftSize = 256;
      analyserRef.current = analyser;

      // Determine the best supported MIME type
      // Prefer mp4 for iOS compatibility, fallback to webm for Chrome/Firefox
      const mimeType = MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
          ? 'audio/webm;codecs=opus'
          : MediaRecorder.isTypeSupported('audio/webm')
            ? 'audio/webm'
            : 'audio/wav';
      
      console.log('🎤 Using audio format:', mimeType);

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        
        // Stop visualization
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        setAudioLevel(0);
      };

      mediaRecorder.start(100); // Collect data every 100ms
      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);

      // Start timer with max duration limit
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          // Auto-stop at max duration
          if (newTime >= MAX_RECORDING_SECONDS) {
            // Use setTimeout to avoid calling stopRecording during state update
            setTimeout(() => {
              if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop();
              }
              if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
              }
              if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
              }
              setIsRecording(false);
              setIsPaused(false);
            }, 0);
            return MAX_RECORDING_SECONDS;
          }
          return newTime;
        });
      }, 1000);

      // Start visualization
      visualizeAudio();

    } catch (error) {
      console.error('Error accessing microphone:', error);
      setPermissionDenied(true);
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }

    setIsRecording(false);
    setIsPaused(false);
  };

  // Toggle play/pause for recorded audio
  const togglePlayback = () => {
    if (!audioPlayerRef.current) return;

    if (isPlaying) {
      audioPlayerRef.current.pause();
    } else {
      audioPlayerRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  // Handle audio ended
  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  // Handle save and upload
  const handleSave = () => {
    if (!audioBlob) return;

    // Create a File object from the Blob
    const fileName = `voice_message_${Date.now()}.webm`;
    const file = new File([audioBlob], fileName, { type: audioBlob.type });

    // Use only name as note
    const note = nameText.trim() || null;

    onSave(file, note);
    onClose();
  };

  // Handle cancel
  const handleCancel = () => {
    cleanup();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-3xl p-6 max-w-md w-full mx-4 shadow-2xl">
        
        {/* Modal Header */}
        <div className="text-center mb-6">
          <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center transition-all duration-300 ${
            isRecording 
              ? 'bg-gradient-to-br from-red-400 to-red-600 animate-pulse' 
              : audioBlob 
                ? 'bg-gradient-to-br from-green-400 to-green-600'
                : 'bg-gradient-to-br from-pink-100 to-purple-100'
          }`}>
            <FaMicrophone className={`text-3xl ${
              isRecording ? 'text-white' : audioBlob ? 'text-white' : 'text-pink-600'
            }`} />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-1">
            {t('uploadPage.recordVoice')}
          </h3>
          <p className="text-gray-500">
            {isRecording 
              ? t('uploadPage.recording')
              : audioBlob 
                ? t('uploadPage.recordingComplete')
                : t('uploadPage.recordVoiceDesc')}
          </p>
        </div>

        {/* Permission Denied Warning */}
        {permissionDenied && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-2xl">
            <p className="text-red-600 text-sm text-center">
              {t('uploadPage.microphonePermission')}
            </p>
          </div>
        )}

        {/* Recording Timer and Visualization */}
        <div className="mb-6">
          <div className="text-center">
            <div className="text-4xl font-mono font-bold text-gray-900 mb-2">
              {formatTime(recordingTime)}
            </div>
            {isRecording && (
              <div className="mb-4">
                <div className="text-sm text-gray-500 mb-1">
                  {t('uploadPage.maxDuration')}: {formatTime(MAX_RECORDING_SECONDS)}
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-orange-400 to-red-500 transition-all duration-1000 ease-linear"
                    style={{ width: `${(recordingTime / MAX_RECORDING_SECONDS) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {/* Audio Level Visualization */}
            {isRecording && (
              <div className="flex items-center justify-center gap-1 h-12 mb-4">
                {[...Array(12)].map((_, i) => (
                  <div
                    key={i}
                    className="w-2 bg-gradient-to-t from-pink-500 to-purple-500 rounded-full transition-all duration-75"
                    style={{
                      height: `${Math.max(8, audioLevel * 48 * (1 + Math.sin(i * 0.5 + Date.now() / 100) * 0.3))}px`,
                      opacity: 0.6 + audioLevel * 0.4
                    }}
                  />
                ))}
              </div>
            )}

            {/* Audio Player for playback */}
            {audioBlob && audioUrl && (
              <div className="mb-4">
                <audio 
                  ref={audioPlayerRef} 
                  src={audioUrl} 
                  onEnded={handleAudioEnded}
                  className="hidden"
                />
                <button
                  onClick={togglePlayback}
                  className="w-14 h-14 mx-auto bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white rounded-full flex items-center justify-center transition-all transform hover:scale-110"
                >
                  {isPlaying ? <FaPause className="text-xl" /> : <FaPlay className="text-xl ml-1" />}
                </button>
                <p className="text-sm text-gray-500 mt-2">
                  {t('uploadPage.tapToListen')}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Name Input (shown after recording) */}
        {audioBlob && (
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t('uploadPage.tellUsWhoYouAre')}
            </label>
            <input
              type="text"
              value={nameText}
              onChange={(e) => setNameText(e.target.value)}
              placeholder={t('uploadPage.namePlaceholder')}
              className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-pink-500 transition-colors text-gray-800 placeholder-gray-400"
              maxLength={50}
            />
            <div className="text-right text-xs text-gray-400 mt-1">
              {nameText.length}/50
            </div>
          </div>
        )}

        {/* Control Buttons */}
        <div className="flex gap-3">
          {!isRecording && !audioBlob && (
            <>
              <button
                onClick={handleCancel}
                className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-2xl transition-colors"
              >
                {t('uploadPage.cancel')}
              </button>
              <button
                onClick={startRecording}
                disabled={!uploadsEnabled}
                className="flex-1 py-3 px-4 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-semibold rounded-2xl transition-all transform hover:scale-105 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaMicrophone />
                {t('uploadPage.startRecording')}
              </button>
            </>
          )}

          {isRecording && (
            <button
              onClick={stopRecording}
              className="flex-1 py-3 px-4 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold rounded-2xl transition-all transform hover:scale-105 flex items-center justify-center gap-2"
            >
              <FaStop />
              {t('uploadPage.stopRecording')}
            </button>
          )}

          {audioBlob && !isRecording && (
            <>
              <button
                onClick={() => {
                  setAudioBlob(null);
                  setAudioUrl(null);
                  setRecordingTime(0);
                }}
                className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-2xl transition-colors"
              >
                {t('uploadPage.recordAgain')}
              </button>
              <button
                onClick={handleSave}
                className="flex-1 py-3 px-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold rounded-2xl transition-all transform hover:scale-105 flex items-center justify-center gap-2"
              >
                <FaUpload />
                {t('uploadPage.saveRecording')}
              </button>
            </>
          )}
        </div>

        {/* Close button */}
        <button
          onClick={handleCancel}
          className="absolute top-4 right-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
        >
          <FaTimes className="text-gray-500" />
        </button>
      </div>
    </div>
  );
};

export default AudioRecorder;
